import React, { useRef, useState } from 'react';
import './RespondToSuggestion.css';

// Static, no-backend mockup of a "v6" /respond page — a plain vertical
// stack of cards (no arcade, no typewriter — everything renders
// immediately, same as the original v1/v2 layout) but with the
// group-chat/1:1 question working exactly as it currently does live in v3:
// it appears inline inside whichever card was most recently confirmed
// interested, as soon as there are 2+ picks, using tick-box style buttons
// — and it's sticky (stays on that card, still naming everyone picked so
// far) rather than living as a single question at the bottom of the page.
// Nothing on this page saves anything. Visit /respond-v6-preview to view it.

const MOCK_COMMUNITY = 'UK Golfers';
const MOCK_RECIPIENT_FIRST_NAME = 'James';

const MOCK_PEOPLE = [
  {
    userId: 'mock-1',
    firstName: 'Henry',
    age: 36,
    location: 'Brighton',
    lookingFor: 'Society or roll-up group',
    bio: 'Coming back to golf after a long break. Rusty swing, keen attitude, looking for relaxed practice partners.',
    initials: 'H',
    color: '#0ea5e9',
  },
  {
    userId: 'mock-2',
    firstName: 'Alfie',
    age: 42,
    location: 'Birmingham',
    lookingFor: 'Just a casual round, Competition partners',
    bio: 'Retired a couple of years ago and now play three times a week if the weather lets me. Happy to play with anyone friendly.',
    initials: 'A',
    color: '#f97316',
  },
  {
    userId: 'mock-3',
    firstName: 'Charlotte',
    age: 42,
    location: 'Edinburgh',
    lookingFor: 'Just a casual round, Society or roll-up group',
    bio: 'Mid-teens handicap and trying to get it down before the club championship. Always up for a weekend round.',
    initials: 'C',
    color: '#8b5cf6',
  },
];

const joinNames = (names) => {
  const filtered = names.filter(Boolean);
  if (filtered.length === 0) return '';
  if (filtered.length === 1) return filtered[0];
  return `${filtered.slice(0, -1).join(', ')} and ${filtered[filtered.length - 1]}`;
};

const PersonCard = ({ person, status, onConnect, onNotNow, showGroupQuestion, groupQuestionNames, groupAnswer, onGroupAnswer }) => (
  <div className="respond-card">
    <div className="respond-photo-gallery">
      <div className="respond-avatar-mock" style={{ background: person.color }}>{person.initials}</div>
    </div>
    <div className="respond-details">
      <div className="respond-headline">
        <strong>{person.firstName}</strong>
        <span className="respond-age">{person.age}</span>
        <span className="respond-meta"> · {person.location}</span>
      </div>
      {person.lookingFor && <div className="respond-meta"><strong>Looking For:</strong> {person.lookingFor}</div>}
      {person.bio && <div className="respond-bio">{person.bio}</div>}

      <div className="respond-actions">
        <button type="button" className={status === 'interested' ? 'button' : 'buttonSecondary'} onClick={onConnect}>
          {status === 'interested' ? '✓ Connect' : 'Connect'}
        </button>
        <button type="button" className={status === 'not_interested' ? 'button' : 'buttonSecondary'} onClick={onNotNow}>
          {status === 'not_interested' ? '✓ Not now' : 'Not now'}
        </button>
      </div>

      {status === 'interested' && (
        <div className="respond-v4-hello-sent">
          We'll double check with {person.firstName}, and then intro you over messenger.
        </div>
      )}

      {showGroupQuestion && (
        <div className="respond-group-inline" style={{ marginTop: '0.9rem' }}>
          <div className="respond-intro" style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
            Also, would you prefer to be introduced:
          </div>
          <div className="respond-actions respond-actions--sub">
            <button type="button" className="respond-tick-btn" onClick={() => onGroupAnswer(false)}>
              <span className={`respond-tickbox${groupAnswer === false ? ' respond-tickbox--checked' : ''}`} />
              <span className="respond-tick-label">1:1 Individually</span>
            </button>
            <button type="button" className="respond-tick-btn" onClick={() => onGroupAnswer(true)}>
              <span className={`respond-tickbox${groupAnswer === true ? ' respond-tickbox--checked' : ''}`} />
              <span className="respond-tick-label">In a group ({groupQuestionNames})</span>
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
);

const RespondV6Preview = () => {
  const [actionState, setActionState] = useState({});
  const [interestedOrder, setInterestedOrder] = useState([]);
  const [groupAnswer, setGroupAnswer] = useState(null);
  const anchorRef = useRef(null);

  const handleConnect = (userId) => {
    setActionState((prev) => ({ ...prev, [userId]: 'interested' }));
    setInterestedOrder((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
  };
  const handleNotNow = (userId) => {
    setActionState((prev) => ({ ...prev, [userId]: 'not_interested' }));
    setInterestedOrder((prev) => prev.filter((id) => id !== userId));
  };

  // Sticky anchor — same pattern as the live v3 page: once the group
  // question appears under a card it stays there (even as more people get
  // picked, it just keeps naming everyone), only moving on if that
  // specific card's pick gets reversed (Not now).
  const settledInterestedIds = interestedOrder.filter((id) => actionState[id] === 'interested');
  if (settledInterestedIds.length >= 2) {
    const anchorStillValid = anchorRef.current && actionState[anchorRef.current] === 'interested';
    if (!anchorStillValid) anchorRef.current = settledInterestedIds[settledInterestedIds.length - 1];
  } else {
    anchorRef.current = null;
  }
  const groupQuestionAnchorId = anchorRef.current;
  const groupQuestionNames = joinNames(
    settledInterestedIds.map((id) => MOCK_PEOPLE.find((p) => p.userId === id)?.firstName)
  );

  return (
    <div className="respond-page">
      <div className="respond-page-inner">
        <div className="respond-greeting">Hi {MOCK_RECIPIENT_FIRST_NAME}!</div>
        <div className="respond-letter">
          <h1 className="respond-heading">
            These people are keen to connect in {MOCK_COMMUNITY}.
          </h1>
          <div className="respond-intro">
            Would you like us to put you in touch?
          </div>
          <div className="respond-intro-sub">
            We won't share your contact details (we intro you over messenger).
          </div>
        </div>

        <div className="respond-people">
          {MOCK_PEOPLE.map((p) => (
            <PersonCard
              key={p.userId}
              person={p}
              status={actionState[p.userId]}
              onConnect={() => handleConnect(p.userId)}
              onNotNow={() => handleNotNow(p.userId)}
              showGroupQuestion={p.userId === groupQuestionAnchorId}
              groupQuestionNames={groupQuestionNames}
              groupAnswer={groupAnswer}
              onGroupAnswer={setGroupAnswer}
            />
          ))}
        </div>

        <div className="respond-done respond-fade-in respond-fade-in--slow">
          <button type="button" className="button-fancy" disabled>
            Close window
          </button>
          <div className="respond-done-note">This is a preview — nothing here is saved.</div>
        </div>
      </div>
    </div>
  );
};

export default RespondV6Preview;
